import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, FileText, Filter, X, Sparkles, BookOpen, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  useRequirements,
  useReqSpecs,
  useCreateRequirement,
  useDeleteRequirement,
} from '@/features/requirements/api/useRequirements'
import { useProjects } from '@/features/projects/api/useProjects'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { formatDate } from '@/lib/utils'
import { logger } from '@/lib/utils/logger'
import {
  RequirementStatus,
  getRequirementStatusLabel,
  getRequirementStatusColor,
  getRequirementTypeLabel,
  RequirementType,
} from '@/types/requirement.types'
import type { Requirement } from '@/types/requirement.types'
import { DRDList } from '../components/DRDList'

const RequirementsPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Check URL parameters
  const drdId = searchParams.get('drd_id')
  const srsIdFromUrl = searchParams.get('srs_id')

  // Filters
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all')
  const [selectedSrsId, setSelectedSrsId] = useState<string>(srsIdFromUrl || 'all')
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState<string>(drdId ? 'ai' : 'manual')

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newReqName, setNewReqName] = useState('')
  const [newReqDocId, setNewReqDocId] = useState('')
  const [newReqSrsId, setNewReqSrsId] = useState<string>('')

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<Requirement | null>(null)

  // Switch tabs based on URL parameters
  useEffect(() => {
    if (drdId) {
      setActiveTab('ai')
    } else if (srsIdFromUrl) {
      setActiveTab('manual')
      setSelectedSrsId(srsIdFromUrl)
    }
  }, [drdId, srsIdFromUrl])

  // Fetch data
  const { data: projects } = useProjects()
  const { data: reqSpecs } = useReqSpecs(
    selectedProjectId !== 'all'
      ? { project_id: parseInt(selectedProjectId, 10) }
      : {}
  )
  const { data: requirements, isLoading: reqLoading } = useRequirements(
    selectedSrsId !== 'all'
      ? { srs_id: parseInt(selectedSrsId, 10) }
      : selectedProjectId !== 'all'
      ? { project_id: parseInt(selectedProjectId, 10) }
      : {}
  )

  const createMutation = useCreateRequirement()
  const deleteMutation = useDeleteRequirement()

  // Filter requirements by search term
  const filteredRequirements = useMemo(() => {
    if (!requirements) return []
    if (!searchTerm) return requirements
    const q = searchTerm.toLowerCase()
    return requirements.filter(
      (req) =>
        req.name.toLowerCase().includes(q) ||
        req.req_doc_id.toLowerCase().includes(q)
    )
  }, [requirements, searchTerm])

  const hasFilters =
    selectedProjectId !== 'all' || selectedSrsId !== 'all' || searchTerm !== ''

  const clearFilters = () => {
    setSelectedProjectId('all')
    setSelectedSrsId('all')
    setSearchTerm('')
  }

  const handleCreate = async () => {
    if (!newReqName.trim() || !newReqDocId.trim() || !newReqSrsId) {
      toast.warning('Please fill in all required fields')
      return
    }

    try {
      await createMutation.mutateAsync({
        name: newReqName.trim(),
        req_doc_id: newReqDocId.trim(),
        srs_id: parseInt(newReqSrsId, 10),
      })
      toast.success('Requirement created successfully')
      setCreateDialogOpen(false)
      setNewReqName('')
      setNewReqDocId('')
      setNewReqSrsId('')
    } catch (err) {
      logger.error('Failed to create requirement', err)
      toast.error('Failed to create requirement. Please try again.')
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      toast.success(`Requirement "${deleteTarget.name}" deleted`)
      setDeleteTarget(null)
    } catch (err) {
      logger.error('Failed to delete requirement', err)
      toast.error('Failed to delete requirement. Please try again.')
    }
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Requirements</h1>
          <p className="text-muted-foreground">
            Manage requirements and AI-generated design requirements documents
          </p>
        </div>

        {/* Create Requirement Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Requirement
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Requirement</DialogTitle>
              <DialogDescription>
                Add a new requirement to a specification
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="req-spec">Specification</Label>
                <Select value={newReqSrsId} onValueChange={setNewReqSrsId}>
                  <SelectTrigger id="req-spec">
                    <SelectValue placeholder="Select specification" />
                  </SelectTrigger>
                  <SelectContent>
                    {reqSpecs?.map((spec) => (
                      <SelectItem key={spec.id} value={spec.id.toString()}>
                        {spec.name} ({spec.doc_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="req-name">Name</Label>
                <Input
                  id="req-name"
                  placeholder="Requirement name"
                  value={newReqName}
                  onChange={(e) => setNewReqName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="req-doc-id">Document ID</Label>
                <Input
                  id="req-doc-id"
                  placeholder="e.g. REQ-001"
                  value={newReqDocId}
                  onChange={(e) => setNewReqDocId(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs for Manual Requirements and AI-Generated DRDs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="manual" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Manual Requirements
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            AI-Augmented DRDs
          </TabsTrigger>
        </TabsList>

        {/* ── Manual Requirements Tab ──────────────────────────────────── */}
        <TabsContent value="manual" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    Filters
                  </CardTitle>
                  <CardDescription>
                    Filter requirements by project, specification, or search
                  </CardDescription>
                </div>
                {hasFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="mr-2 h-4 w-4" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Project</Label>
                  <Select
                    value={selectedProjectId}
                    onValueChange={(value) => {
                      setSelectedProjectId(value)
                      setSelectedSrsId('all')
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Projects</SelectItem>
                      {projects?.map((project) => (
                        <SelectItem key={project.id} value={project.id.toString()}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Specification</Label>
                  <Select
                    value={selectedSrsId}
                    onValueChange={setSelectedSrsId}
                    disabled={selectedProjectId === 'all'}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Specifications</SelectItem>
                      {reqSpecs?.map((spec) => (
                        <SelectItem key={spec.id} value={spec.id.toString()}>
                          {spec.name} ({spec.doc_id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Search</Label>
                  <Input
                    placeholder="Search requirements..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Requirements Table */}
          <Card>
            <CardHeader>
              <CardTitle>
                Requirements List ({filteredRequirements.length})
              </CardTitle>
              <CardDescription>
                Click on a requirement to view details, versions, and test coverage
              </CardDescription>
            </CardHeader>
            <CardContent>
              {reqLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredRequirements.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Doc ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Spec</TableHead>
                        <TableHead>Coverage</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="w-[80px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRequirements.map((req) => (
                        <TableRow
                          key={req.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/requirements-ops/requirements/${req.id}`)}
                        >
                          <TableCell>
                            <span className="font-mono text-sm">{req.req_doc_id}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-blue-500" />
                              <span className="font-medium">{req.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {req.type ? (
                              <Badge variant="outline">
                                {getRequirementTypeLabel(req.type as RequirementType)}
                              </Badge>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            {req.status ? (
                              <Badge variant={getRequirementStatusColor(req.status as RequirementStatus)}>
                                {getRequirementStatusLabel(req.status as RequirementStatus)}
                              </Badge>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {req.srs_name || '—'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={req.coverage_count && req.coverage_count > 0 ? 'default' : 'secondary'}>
                              {req.coverage_count ?? 0} test{req.coverage_count === 1 ? '' : 's'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {formatDate(req.created_at)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeleteTarget(req)
                              }}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-semibold">No Requirements Found</h3>
                  <p className="text-sm text-gray-500 mt-2">
                    {hasFilters
                      ? 'Try adjusting your filters'
                      : 'Get started by creating your first requirement'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── AI-Augmented DRDs Tab ────────────────────────────────────── */}
        <TabsContent value="ai" className="space-y-6">
          <DRDList
            projectId={selectedProjectId !== 'all' ? parseInt(selectedProjectId, 10) : undefined}
            drdId={drdId ? parseInt(drdId, 10) : undefined}
          />
        </TabsContent>
      </Tabs>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}
        title="Delete Requirement"
        description={`Are you sure you want to delete "${deleteTarget?.name}" (${deleteTarget?.req_doc_id})? This will also remove all versions and coverage links. This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}

export default RequirementsPage
